/**
 * A map where each key is a 'pattern' (string or regex) to search for,
 * and each value is the corresponding 'replacementText' (string) to replace it with.
 * Regular expressions are supported for pattern matching.
 */
export type TextReplacements = Record<string, string>;

export const applyTextReplacements = (originalText: string, textReplacements: TextReplacements): string => {
  let result = originalText;

  Object.entries(textReplacements).forEach(([pattern, replacement]) => {
    const replaceWhileMatchingCasing = (match: string) => {
      if (match === match.toUpperCase()) {
        return replacement.toUpperCase();
      }
      if (match[0] === match[0].toUpperCase()) {
        return replacement[0].toUpperCase() + replacement.slice(1).toLowerCase();
      }
      return replacement.toLowerCase();
    };

    // Wrap pattern in additional regex to ensure pattern is:
    // - Not within curly braces, e.g. {premium}
    // - Not part of a larger token
    // Numeric patterns (e.g. a year) must not match when embedded in a larger number — an ID
    // or digit run like "120261" — so anchor them on digit boundaries. Non-numeric patterns
    // keep their existing guards.
    const isNumericPattern = /^\d+$/.test(pattern);
    const before = isNumericPattern ? '(?<!\\d)' : '(?<!w)';
    const after = isNumericPattern ? '(?!\\d)' : '(?!w)';
    const regexPattern = new RegExp(`(?<!{)${before}${pattern}${after}(?!})`, 'gi');
    result = result.replace(regexPattern, replaceWhileMatchingCasing);
  });

  return result;
};

// Contentful may switch a hardcoded federal-limit year (e.g. "The 2026 limits are…") to a
// "{year}" placeholder at any time without a matching code change, so both forms must
// keep working.
const YEAR_PLACEHOLDER = '{year}';

export const makeYearReplacement = (
  selectedPlanYear: string | undefined,
  hardcodedYear: string,
): TextReplacements | undefined =>
  selectedPlanYear ? { [YEAR_PLACEHOLDER]: selectedPlanYear, [hardcodedYear]: selectedPlanYear } : undefined;

export const PREMIUM_TEXT_REPLACEMENT = {
  'en-US': {
    premiums: 'contributions',
    premium: 'contribution',
  },
  'es-US': {
    primas: 'contribuciones',
    prima: 'contribución',
  },
};
